import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Transfer, SearchBox, Button, Table } from 'tea-component';

import useGameList, {
  mergeSearchGameList,
  useLevelUpGameList,
} from '@/pages/opinion/hooks/useGameList';
import RegionCountryItem from '@/pages/opinion/components/Snippets/RegionCountryItem';
import { useIntl } from 'umi';
import { Bubble } from 'tea-component/es/bubble';
import RenderGameIcon from '@/pages/opinion/components/RenderImg/renderGameIcon';

import { initialUpperCase } from '@/pages/opinion/utils';
import useGameCountryPublisher from '../../../../hooks/useGameCountryPublisher';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import _ from 'lodash-es';
import Tips from '@op/components/Snippets/Tips';
import { NO_DATE } from '@op/constants';

// 弹框
const { selectable, removeable, scrollable, draggable, autotip } = Table.addons;

const columns = (gameCountryMap?: any) => [
  {
    key: '',
    header: '',
    render: (cvm: any) => {
      let {
        publisher,
        entity_name,
        cover,
        country,
        entity_type,
        edition_unified_id,
      } = cvm;
      if ((!country || !publisher) && gameCountryMap) {
        let data = gameCountryMap[edition_unified_id];
        publisher = data?.publisher;
        country = data?.country;
      }

      return (
        <div className="add-games-flex">
          <div className="games-img">
            <RenderGameIcon
              gameInfo={{
                entity_type: entity_type,
                cover: cover,
                edition_unified_id,
              }}
              className="game-img-icon"
            ></RenderGameIcon>
          </div>
          <div className="games-context">
            <div className="games-title">{entity_name}</div>
            <div className="games-desc">
              <RegionCountryItem
                iconClassName="games-svg"
                showText={false}
                market={country}
              ></RegionCountryItem>
              <span className="games-text">
                {publisher && initialUpperCase(publisher)}
              </span>
            </div>
          </div>
        </div>
      );
    },
  },
];

export default function AddCompare({
  visible,
  close,
  confirm,
  selected,
  mainGame,
  selectedLoading = false,
}: {
  visible: boolean;
  close: () => void;
  selected: Array<any>;
  mainGame: any;
  confirm: (targetList: Array<any>) => void;
  selectedLoading?: boolean;
}) {
  const { formatMessage } = useIntl();
  const [targetList, setTargetList] = useState<any>([]);
  const [inputValue, setInputValue] = useState<any>('');

  const { games, loading, allLoading, allGameList } = useGameList({
    entity_name: inputValue,
    source_type: 'all',
    allList: true,
    otherParams: {
      is_only_unified_id: true,
      need_country_publisher: true,
      need_second_country_publisher: true,
    },
    cancelKey: 'add-compare',
  });

  useEffect(() => {
    setTargetList(selected);
  }, [selected]);

  const { gameCountryMap } = useGameCountryPublisher({ selected, mainGame });

  const renderGames = useMemo(() => {
    if (inputValue === '') return games;
    const localList = mergeSearchGameList({
      gameList: allGameList,
      excludeGameList: games,
      search: inputValue,
    });

    return [...games, ...localList];
  }, [allGameList, games, inputValue]);

  const changeInputSearchText = useCallback(
    _.debounce((value: string) => {
      setInputValue(value);
    }, 500),
    [],
  );

  return (
    <Modal
      className="dbo-add-compare"
      size={800}
      visible={visible}
      caption={formatMessage({ id: 'Add Games to Compare' })}
      onClose={close}
    >
      <Modal.Body>
        <Transfer
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'Add' })}
              header={
                <SearchBox
                  disabled={allLoading}
                  placeholder={formatMessage({ id: 'Search any game' })}
                  onChange={(value) => changeInputSearchText(value)}
                  onSearch={(value) => changeInputSearchText(value)}
                />
              }
            >
              <SourceTable
                loading={loading || allLoading}
                dataSource={renderGames}
                targetKeys={targetList.map(
                  (item: any) => item.edition_unified_id,
                )}
                disabledId={mainGame.edition_unified_id}
                onChange={(keys: any, { selectedRecords }: any) => {
                  const otherKey = [...keys];
                  const newTargetList = targetList.filter((item: any) => {
                    let index = otherKey.indexOf(item.edition_unified_id);
                    if (index > -1) {
                      otherKey.splice(index, 1);
                      return true;
                    }
                    return false;
                  });
                  const otherRecords =
                    otherKey.length === 0
                      ? []
                      : selectedRecords.filter((item: any) =>
                          otherKey.includes(item.edition_unified_id),
                        );
                  setTargetList([
                    ...newTargetList,
                    ...otherRecords.map((item: any) => ({
                      ...item,
                      selected_edition_unified_id: item.edition_unified_id,
                    })),
                  ]);
                }}
              />
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              scrollable={false}
              title={
                <div className="text-row-show">
                  {`${formatMessage({ id: 'Selected' })} (${
                    targetList.length
                  }/9)`}
                  {targetList.length > 0 ? (
                    <Button
                      type="link"
                      className="text-clear"
                      onClick={() => setTargetList([])}
                    >
                      {formatMessage({ id: 'Clear All' })}
                    </Button>
                  ) : null}
                </div>
              }
            >
              <TargetTable
                loading={selectedLoading}
                dataSource={targetList}
                mainGame={mainGame}
                onRemove={(key: any) => {
                  setTargetList(
                    targetList.filter((i: any) => i.edition_unified_id !== key),
                  );
                }}
                gameCountryMap={gameCountryMap}
                onDragEnd={(records: Array<any>) => {
                  setTargetList(records);
                }}
              />
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            confirm(targetList);
            close();
            buriedBtnPoint('ConfirmGameSelection');
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function AddLevelUpGameCompare({
  visible,
  close,
  confirm,
  selected,
  selectedLoading = false,
  params = [],
  mainGame,
}: {
  visible: boolean;
  close: () => void;
  selected: Array<any>;
  confirm: (targetList: Array<any>) => void;
  selectedLoading?: boolean;
  params?: Array<any>;
  mainGame: any;
}) {
  const { formatMessage } = useIntl();
  const [targetList, setTargetList] = useState<any>([]);

  const {
    loading,
    setLoading,
    gameList,
    changePageInfo,
    setSearchValue,
    nextPage,
    pageInfo,
    total,
    request,
  } = useLevelUpGameList({
    filter: params,
  });

  useEffect(() => {
    setTargetList(selected);
  }, [selected]);

  const { gameCountryMap } = useGameCountryPublisher({
    selected,
    mainGame
  });
 

  const changeInputSearchText = useCallback(
    _.debounce((value: string) => {
      changePageInfo({ page: 1, page_size: 10 });
      setSearchValue(value);
    }, 500),
    [],
  );

  const changeNextPage = async () => {
    if (pageInfo.page * pageInfo.page_size < total) {
      setLoading(true);
      const newPageInfo = {
        page: pageInfo.page + 1,
        page_size: pageInfo.page_size,
      };
      const hasData = await request(newPageInfo);
      if (hasData) {
        nextPage(newPageInfo);
      }
    }
  };
  return (
    <Modal
      className="dbo-add-compare"
      size={800}
      visible={visible}
      caption={formatMessage({ id: 'Add Games to Compare' })}
      onClose={close}
    >
      <Modal.Body>
        <Transfer
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'Add' })}
              header={
                <SearchBox
                  placeholder={formatMessage({ id: 'Search any game' })}
                  onChange={(value) => changeInputSearchText(value)}
                  onSearch={(value) => changeInputSearchText(value)}
                />
              }
            >
              <SourceTable
                loading={loading}
                dataSource={gameList}
                hasPage
                loadingFinish={pageInfo.page * pageInfo.page_size >= total}
                isFirstLoading={pageInfo.page === 1}
                onScrollBottom={changeNextPage}
                targetKeys={targetList.map(
                  (item: any) => item.edition_unified_id,
                )}
                onChange={(keys: any, { selectedRecords }: any) => {
                  const otherKey = [...keys];
                  const newTargetList = targetList.filter((item: any) => {
                    let index = otherKey.indexOf(item.edition_unified_id);
                    if (index > -1) {
                      otherKey.splice(index, 1);
                      return true;
                    }
                    return false;
                  });
                  const otherRecords =
                    otherKey.length === 0
                      ? []
                      : selectedRecords.filter((item: any) =>
                          otherKey.includes(item.edition_unified_id),
                        );
                  setTargetList([
                    ...newTargetList,
                    ...otherRecords.map((item: any) => ({
                      ...item,
                      selected_edition_unified_id: item.edition_unified_id,
                    })),
                  ]);
                }}
              />
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              scrollable={false}
              title={
                <div className="text-row-show">
                  {`${formatMessage({ id: 'Selected' })} (${
                    targetList.length
                  })`}
                  {targetList.length > 0 ? (
                    <Button
                      type="link"
                      className="text-clear"
                      onClick={() => setTargetList([])}
                    >
                      {formatMessage({ id: 'Clear All' })}
                    </Button>
                  ) : null}
                </div>
              }
            >
              <TargetTable
                mainGame={mainGame}
                gameCountryMap={gameCountryMap}
                loading={selectedLoading}
                dataSource={targetList}
                onRemove={(key: any) => {
                  setTargetList(
                    targetList.filter((i: any) => i.edition_unified_id !== key),
                  );
                }} 
                onDragEnd={(records: Array<any>) => {
                  setTargetList(records);
                }}
              />
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            confirm(targetList);
            close();
            buriedBtnPoint('ConfirmGameSelection');
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function SourceTable({
  dataSource,
  targetKeys,
  onChange,
  loading,
  disabledId,
  onScrollBottom,
  hasPage = false,
  loadingFinish = false,
}: any) {
  const { formatMessage } = useIntl();

  function getScrollableProps() {
    const scrollableProps = {
      virtualizedOptions: {
        itemHeight: 65,
        height: 350,
      },
    };
    if (!hasPage) {
      return scrollableProps;
    }

    return {
      maxHeight: 350,
      onScrollBottom: () => {
        !loading && onScrollBottom?.();
      },
    };
  }

  function addonFunc() {
    const list = [
      scrollable(getScrollableProps()),
      selectable({
        value: targetKeys,
        onChange,
        rowSelect: true,
        render: (ele, record: any) => {
          const disabled = disabledId !== record.rowKey && record.disabled;

          return (
            <Bubble
              dark
              closeOnScroll
              content={
                disabled
                  ? formatMessage({ id: 'You can choose up to 9 games' })
                  : undefined
              }
            >
              {ele}
            </Bubble>
          );
        },
      }),
    ];

    if (!hasPage) {
      list.push(
        autotip({
          isLoading: loading,
          loadingText: '',
        }),
      );
    }

    return list;
  }

  const records = useMemo(() => {
    if (hasPage) {
      return dataSource;
    }

    return loading ? [] : dataSource;
  }, [dataSource, loading]);

  return (
    <Table
      className="dbo-source-tips-table"
      records={records}
      recordKey="edition_unified_id"
      hideHeader={true}
      columns={columns()}
      rowDisabled={(record) =>
        (targetKeys.length >= 9 &&
          !targetKeys.includes(record.edition_unified_id)) ||
        disabledId === record.edition_unified_id
      }
      addons={addonFunc()}
      bottomTip={
        hasPage ? (
          <Tips
            className={
              records.length > 0 ? 'dbo-loading-min' : 'dbo-loading-max'
            }
            loading={loading}
            noData={records.length === 0 || loadingFinish}
            noDateText={loadingFinish ? NO_DATE.LOADING : NO_DATE.TITLE}
          ></Tips>
        ) : null
      }
    />
  );
}
function TargetTable({
  mainGame,
  dataSource,
  onRemove,
  onDragEnd,
  gameCountryMap,
  loading,
}: any) {
  let {
    publisher,
    entity_type,
    entity_name,
    cover,
    country,
    edition_unified_id,
  } = mainGame || {};

  if (!country || !publisher) {
    let data = gameCountryMap[edition_unified_id];
    publisher = data?.publisher;
    country = data?.country; 
  }

  const realDataSource = useMemo(() => {
    return dataSource.filter(
      (item: any) => item.edition_unified_id !== edition_unified_id,
    );
  }, [dataSource, edition_unified_id]);

  return (
    <div className="target-table">
      <div className="add-games-flex target-padding">
        <div className="games-img">
          <RenderGameIcon
            gameInfo={{
              entity_type: entity_type,
              cover: cover,
              edition_unified_id,
            }}
            className="game-img-icon"
          ></RenderGameIcon>
        </div>
        <div className="games-context">
          <div className="games-title">{entity_name}</div>
          <div className="games-desc">
            <RegionCountryItem
              iconClassName="games-svg"
              showText={false}
              market={country}
            ></RegionCountryItem>
            <span className="games-text">
              {publisher && initialUpperCase(publisher)}
            </span>
          </div>
        </div>
      </div>
      <Table
        records={realDataSource}
        hideHeader={true}
        recordKey="edition_unified_id"
        className="dbo-target-table"
        columns={columns(gameCountryMap)}
        addons={[
          scrollable({
            virtualizedOptions: {
              itemHeight: 65,
              height: 320,
            },
          }),
          removeable({ onRemove }),
          draggable({ onDragEnd }),
          autotip({ isLoading: loading }),
        ]}
      />
    </div>
  );
}

function LevelUpTargetTable({
  dataSource,
  onRemove,
  onDragEnd,
  gameCountryMap,
  loading,
}: any) {
  return (
    <div className="target-table">
      <div className="add-games-flex target-padding">
        <div className="games-context">
          <div className="games-title">Games</div>
        </div>
      </div>
      <Table
        records={dataSource}
        hideHeader={true}
        recordKey="edition_unified_id"
        className="dbo-target-table"
        columns={columns(gameCountryMap)}
        addons={[
          scrollable({
            virtualizedOptions: {
              itemHeight: 65,
              height: 320,
            },
          }),
          removeable({ onRemove }),
          draggable({ onDragEnd }),
          autotip({ isLoading: loading }),
        ]}
      />
    </div>
  );
}
